import {DataContext} from "../../contexts/dataContext";
import {useContext, useEffect, useState} from "react";
import {IVehicleSearch} from "../../interfaces/VehicleSearch";
import {basePrice} from "../MakeReservation/pricingCalculations";

export enum FuelType {
    All = 'all_fuel',
    Petrol = 'e95',
    Diesel = 'diesel',
    Electric = 'electric',
    Hybrid = 'hybrid'
}

export enum VehicleType {
    Compact = 'compact', // url param: acriss=1
    StationWagon = 'stw', // ""=2
    SUV = 'suv', // ""=3
    Minibus = 'minibus', // ""=4
    Van = 'van', // ""=5
    ECO = 'eco',
    Parkinghall = 'parkinghall'
}

export enum Country  {
    All = 'all_finland',
    Baltics = 'baltics',
    Scandinavia = 'scandinavia',
    Europe = 'europe'
}

export enum Transmission {
    All = "all_vehicleType",
    Automatic = 'automatic',
    Manual = 'manual'
}

export enum Equipment {
    ParkingPermit = 'parkingPermit',
    TowingHitch = 'towingHitch',
    StuddedTyres = 'studdedTyres',
    SummerTyres = 'summerTyres',
    AllSeason = 'allSeasonTyres'
}

export enum SortOptions {
    Price = 'price',
    Distance = 'distance',
    TrunkVolume = 'trunkvolume'
}

export interface IAcrissCodes {
    compact: string[];
    stw: string[];
    suv: string[];
    minibus: string[];
    van: string[];
}

export interface ITagCloudAvailableCountry {
    baltics: string[];
    scandinavia: string[];
    europe: string[];
}

export interface FilterParameters {
    types?: VehicleType[];
    fuelType?: FuelType;
    selectedTag?: Country;
    transmission?: Transmission;
    tyreTypes?: Equipment[];
    reset?: boolean;
    model?: number | undefined;
}

export const tagCloudAvailableCountry:  ITagCloudAvailableCountry = {
    baltics:  ["Viro", "Latvia", "Liettua", "Estonia", "Latvia", "Lithuania"],
    scandinavia: ["Sweden", "Norway", "Denmark","Ruotsi", "Norja", "Tanska"],
    europe: ["Saksa", "Ranska", "Itävalta", "Sveitsi", "Belgia", "Alankomaat", "Luxemburg", "Germany", "France", "Austria", "Switzerland", "Belgium", "Netherlands", "Luxembourg"],
};

const useFilter = () => {
    const { mapData, filteredVehicles, changeFilterParams,changeFilteredList, acrissCodeFromUrl, modelId, acrissCodeSet } = useContext(DataContext);
    const [filteredList, setFilteredList] = useState<IVehicleSearch[]>([]);
    const [originalList, setOriginalList] = useState<IVehicleSearch[]>(mapData ?? []);

    // Filter Tabs Values
    const [lowestPrice, setLowestPrice] = useState<number>(0);
    const [lowestDistance, setLowestDistance] = useState<number>(0);
    const [highestTrunkVolume, setHighestTrunkVolume] = useState<number>(0);
    // Selected filters
    const [selectedFilters, setSelectedFilters] = useState<FilterParameters>({
        types: acrissCodeFromUrl? [acrissCodeFromUrl] as VehicleType[] : [],
        fuelType: FuelType.All,
        selectedTag: Country.All,
        transmission: Transmission.All,
        tyreTypes: [],
        model: modelId,
        reset: false,
    });

    useEffect(() => {
        if(filteredVehicles && filteredVehicles.length !== 0){
            const lowestDistance = filteredVehicles?.reduce((prev, current) => (prev.distance < current.distance) ? prev : current);
            setLowestDistance(Math.round((+lowestDistance.distance / 1000) * 10) / 10);
            const lowestPriceVehicle = filteredVehicles?.reduce((prev, current) => (prev.pricingData.base.totalPrice < current.pricingData.base.totalPrice) ? prev : current);
            if (lowestPriceVehicle) {
                setLowestPrice(basePrice(lowestPriceVehicle));
            }
            const highestVolumeVehicle = filteredVehicles?.reduce((prev, current) => (prev.vehicleModelData?.dimensions?.trunkVolume < current.vehicleModelData?.dimensions?.trunkVolume) ? current : prev);
            if (highestVolumeVehicle?.vehicleModelData?.dimensions?.trunkVolume !== undefined) {
                setHighestTrunkVolume(highestVolumeVehicle.vehicleModelData.dimensions.trunkVolume);
            }
        }
    }, [filteredVehicles]);

    useEffect(() => {
        if (mapData) setOriginalList(mapData);
    }, [mapData]);

    const resetFilters = () => {
        changeFilterParams({types: [], fuelType: FuelType.All, selectedTag: Country.All, transmission: Transmission.All, tyreTypes: [], model: undefined, reset: true});
        setFilteredList(originalList);
        setSelectedFilters({
            types: [],
            fuelType: FuelType.All,
            selectedTag: Country.All,
            transmission: Transmission.All,
            tyreTypes: [],
            model: undefined,
            reset: true
        });
        changeFilteredList(originalList);
    }

    const sortVehicles = (option: SortOptions) => {
        if(modelId) return; // wait for modelId filtered cars to be set as 'filteredVehicles' state before sorting (once modelId = undefined, this will work fine)

        let sortedVehicles: IVehicleSearch[] = [...(filteredVehicles || [])]; // provide a default empty array when filteredVehicles is undefined
        sortedVehicles.sort((a, b) => {
            // Sort by reserved status first
            if (a.availabilityData?.available && !b.availabilityData?.available) return -1; // if a is reserved and b is not, sort a to the end
            if (!a.availabilityData?.available && b.availabilityData?.available) return 1; // if b is reserved and a is not, sort b to the end
            // Then sort by the selected option
            switch (option) {
                case SortOptions.Distance:
                    return a.distance - b.distance;
                case SortOptions.Price:
                    return a.pricingData.base.totalPrice - b.pricingData.base.totalPrice;
                case SortOptions.TrunkVolume:
                    return b.vehicleModelData!.dimensions!.trunkVolume! - a.vehicleModelData!.dimensions!.trunkVolume!;
            }
        });
        changeFilteredList(sortedVehicles);
        return sortedVehicles;
    };

    const handleFilters = ({types, fuelType, selectedTag, transmission, tyreTypes, model}: FilterParameters): IVehicleSearch[] | undefined => {
    let newFilteredList: IVehicleSearch[] = originalList.filter((vehicle) => {
        let isValid = true;

        if (model) {
            isValid = isValid && vehicle.vehicleModelData.id === model;
        }

        if (types && types.length > 0) {
            isValid = isValid && types.some(type => {
                switch (type) {
                    case VehicleType.StationWagon:
                        return acrissCodeSet?.stw.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.SUV:
                        return acrissCodeSet?.suv.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.Minibus:
                        return acrissCodeSet?.minibus.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.Van:
                        return (vehicle.vehicleModelData.type === "van");
                    case VehicleType.Compact:
                        return acrissCodeSet?.compact.includes(vehicle.vehicleModelData.modelacriss);
                    case VehicleType.ECO:
                        return vehicle.vehicleData.hybridity === 'normal' || vehicle.vehicleData.fuelType === 2;
                    case VehicleType.Parkinghall:
                        return vehicle.homeLocationData.inside === true;
                    default:
                        return false;
                }
            });
        }

        if (fuelType) {
            switch (fuelType) {
                case FuelType.Diesel:
                    isValid = isValid && vehicle.vehicleData.fuelType === 0 && (vehicle.vehicleData.hybridity === 'none' || vehicle.vehicleData.hybridity === 'normal');
                    break;
                case FuelType.Petrol:
                    isValid = isValid && vehicle.vehicleData.fuelType === 1 && (vehicle.vehicleData.hybridity === 'none' || vehicle.vehicleData.hybridity === 'normal');
                    break;
                case FuelType.Hybrid:
                    isValid = isValid && (vehicle.vehicleData.hybridity === 'normal' || vehicle.vehicleData.hybridity === 'plug-in');
                    break;
                case FuelType.Electric:
                    isValid = isValid && vehicle.vehicleData.fuelType === 2;
                    break;
            }
        }

        if (selectedTag) {
            switch (selectedTag) {
                case Country.Baltics:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.balticStates === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.baltics.includes(tag)));
                    break;
                case Country.Scandinavia:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.scandinavia === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.scandinavia.includes(tag)));
                    break;
                case Country.Europe:
                    isValid = isValid && (vehicle.vehicleData.exportPermits.europe === true || vehicle.tagCloud.some(tag => tagCloudAvailableCountry.europe.includes(tag)));
                    break;
            }
        }

        if (transmission) {
            switch (transmission) {
                case Transmission.Automatic:
                    isValid = isValid && vehicle.vehicleData.hasAutomaticTransmission;
                    break;
                case Transmission.Manual:
                    isValid = isValid && !vehicle.vehicleData.hasAutomaticTransmission;
                    break;
            }
        }

        if (tyreTypes && tyreTypes.length > 0) {
            isValid = isValid && tyreTypes.some(tyreType => {
                switch (tyreType) {
                    case Equipment.TowingHitch:
                        return vehicle.vehicleData.hasHitch;
                    case Equipment.StuddedTyres:
                        return vehicle.vehicleData.tyreType === 'nasta';
                    case Equipment.AllSeason:
                        return vehicle.vehicleData.tyreType === 'kitka';
                    case Equipment.SummerTyres:
                        return vehicle.vehicleData.tyreType === 'kesä';
                    case Equipment.ParkingPermit:
                        return vehicle.parkingPermit.valid;
                    default:
                        return false;
                }
            });
        }

        return isValid;
    });

    changeFilteredList(newFilteredList);
    changeFilterParams({types, fuelType, selectedTag, transmission, tyreTypes, model, reset: false});
    setFilteredList(newFilteredList);
    setSelectedFilters({types, fuelType, selectedTag, transmission, tyreTypes, model, reset: false});

    return newFilteredList;
}

    return { resetFilters, handleFilters, sortVehicles, selectedFilters,  filteredList, lowestPrice, lowestDistance, highestTrunkVolume};
};

export default useFilter;